// Browsers leave File.type empty for the formats they do not recognize — .ai
// and .psd among the ones this form accepts — so fall back to the extension.
const MIME_TYPES_BY_EXTENSION = Object.freeze({
  ai: 'application/postscript',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pdf: 'application/pdf',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  psd: 'image/vnd.adobe.photoshop',
  tif: 'image/tiff',
  tiff: 'image/tiff',
})

export const getFileMimeType = ({ name = '', type = '' } = {}) => {
  if (type) return type

  const extensionIndex = name.lastIndexOf('.')
  const extension =
    extensionIndex === -1 ? '' : name.slice(extensionIndex + 1).toLowerCase()
  return MIME_TYPES_BY_EXTENSION[extension] || 'application/octet-stream'
}

const createMetadata = ({ brand = {}, file = {}, tenantId }) => ({
  asset_uploaded_channel: 'EU_API',
  creative_asset_status: 'WIP',
  file_name: file.name || '',
  original_asset_id: null,
  project_id:
    brand.assetHubProjectUuid ||
    brand.assethub_project_uuid ||
    brand.asset_hub_project_uuid ||
    '',
  revision_number: 1,
  source: 'brand_dna',
  tenant_id: tenantId,
})

const createJsonBlob = (value) =>
  new Blob([JSON.stringify(value)], { type: 'application/json' })

export const createSimpleUploadPayload = (context) => {
  const formData = new FormData()
  formData.append('metadata', createJsonBlob(createMetadata(context)))
  formData.append('file', context.file)
  return formData
}

export const createChunkStartPayload = ({ file, tenantId }) => ({
  content_type: getFileMimeType(file),
  file_name: file.name,
  tenant_id: tenantId,
})

export const createChunkPayload = ({
  assetId,
  chunkIndex,
  chunkSize,
  file,
  uploadId,
}) => {
  const chunk = file.slice(
    chunkIndex * chunkSize,
    Math.min((chunkIndex + 1) * chunkSize, file.size),
  )
  const formData = new FormData()
  formData.append(
    'chunkPublishRequest',
    createJsonBlob({
      asset_id: assetId,
      chunk_number: chunkIndex + 1,
      chunk_size: chunk.size,
      upload_id: uploadId,
    }),
  )
  formData.append('file', chunk)
  return formData
}

export const createChunkEndPayload = ({
  assetId,
  brand,
  file,
  jobId,
  partEtags,
  tenantId,
  uploadId,
}) => ({
  asset_id: assetId,
  job_id: jobId,
  metadata: createMetadata({ brand, file, tenantId }),
  part_etags: partEtags,
  upload_id: uploadId,
})

export const mapEnterpriseUploadResponse = (response = {}) => {
  const assetId = response.asset_id || ''
  const renderUrl = response.internal_url || response.render_url || ''

  if (!assetId || !renderUrl) {
    throw new Error('Enterprise Uploads returned an incomplete asset response.')
  }

  // Brand DNA stores where the asset lives in AssetHub; the render URL is the
  // only location Enterprise Uploads returns for a simple upload.
  return { assetId, assetPath: response.asset_path || renderUrl, renderUrl }
}

export const createBrandDocumentPayload = ({
  brand = {},
  brandDocumentId = '',
  documentType = {},
  file = {},
  upload = {},
}) => ({
  assethub_document_metadata: {
    assethub_asset_id: upload.assetId || '',
    assethub_path: upload.assetPath || upload.renderUrl || '',
    file_name: file.name || '',
    mime_type: getFileMimeType(file),
    size_bytes: file.size || 0,
  },
  // Brand DNA assigns this on the first version; a later upload of the same
  // document sends it back to add a version instead of a new document.
  brand_document_id: brandDocumentId || null,
  brand_uuid: brand.id || '',
  document_type_id: documentType.id || '',
})
